import type { UIMessage } from "@ai-sdk/react";
import { SPEC_DATA_PART_TYPE } from "@json-render/core";
import { domToPng } from "modern-screenshot";
import { buildMessageSegments } from "@/lib/utils/aiChatUtils";

export const FIRST_USER_TITLE_MAX_LEN = 80;

export const DEFAULT_NEW_CONVERSATION_TITLE = "New conversation";

/** Non-empty label for sidebar rows (avoids empty string → list item showing raw conversation id). */
export const normalizeHeraldChatRowTitle = (
  title: string | null | undefined,
): string => {
  const trimmed = title?.trim() ?? "";
  return trimmed || DEFAULT_NEW_CONVERSATION_TITLE;
};

/**
 * Picks a title when applying a fetched conversation row onto existing UI state.
 * Prefer a non-empty server `title`; if the API returns blank (common before async titling),
 * keep an in-memory preview (e.g. first user line) instead of overwriting with the default label.
 */
export const mergeFetchedConversationTitle = (
  localTitle: string | null | undefined,
  serverTitle: string | null | undefined,
): string => {
  const serverTrimmed = serverTitle?.trim() ?? "";
  if (serverTrimmed.length > 0) {
    return serverTrimmed.slice(0, FIRST_USER_TITLE_MAX_LEN);
  }
  return normalizeHeraldChatRowTitle(localTitle);
};

/**
 * Sidebar preview when `X-Conversation-Title` is unavailable (aligned with user bubble text rules).
 */
export const extractFirstUserMessageTitle = (messages: UIMessage[]): string => {
  for (const message of messages) {
    if (message.role !== "user") continue;
    const parts = ("parts" in message ? message.parts : []) as Array<
      Record<string, unknown> & { type: string }
    >;
    const segments = buildMessageSegments(parts, SPEC_DATA_PART_TYPE);
    for (const segment of segments) {
      if (segment.kind === "text" && segment.text.trim().length > 0) {
        return segment.text.trim().slice(0, FIRST_USER_TITLE_MAX_LEN);
      }
    }
  }
  return "";
};

/**
 * Resolves sidebar label: `X-Conversation-Title` when present, else first user line preview.
 */
export const resolveHeraldSidebarTitle = (
  messages: UIMessage[],
  getTransportConversationTitle: () => string | null,
): string => {
  const fromHeader = getTransportConversationTitle()?.trim();
  if (fromHeader) {
    return fromHeader.slice(0, FIRST_USER_TITLE_MAX_LEN);
  }
  return (
    extractFirstUserMessageTitle(messages) || DEFAULT_NEW_CONVERSATION_TITLE
  );
};

/**
 * Whether a new `useChat` id should be generated so the pane remounts with fresh state.
 */
export const shouldRemountHeraldChatInstance = (input: {
  prevConversationId: string | undefined;
  conversationId: string | undefined;
  transportConversationId: string | null;
  messagesLength: number;
  initialMessagesLength: number;
}): boolean => {
  const {
    prevConversationId: prev,
    conversationId: cur,
    transportConversationId: tid,
    messagesLength,
    initialMessagesLength,
  } = input;

  if (cur === undefined && prev !== undefined) {
    return true;
  }
  if (cur !== undefined && prev !== undefined && cur !== prev) {
    return true;
  }
  if (cur !== undefined && prev === undefined) {
    const isStreamFirstPersist =
      tid === cur && messagesLength > 0 && initialMessagesLength === 0;
    if (!isStreamFirstPersist) {
      return true;
    }
  }
  return false;
};

/** High-resolution PNG (`domToPng` scale). */
export const HERALD_CHAT_PNG_SCALE_HIGH_RES = 4;

const HERALD_CHAT_PNG_CAPTURE_PADDING_PX = "24px";

const HERALD_CHAT_PNG_CAPTURE_GAP = "0.75rem";

const DEFAULT_PNG_BACKGROUND = "#ffffff";

const resolveCaptureRootWidthPx = (node: HTMLElement): number => {
  if (node.offsetWidth > 0) {
    return node.offsetWidth;
  }
  const parent = node.parentElement;
  if (parent !== null && parent.offsetWidth > 0) {
    return parent.offsetWidth;
  }
  return node.getBoundingClientRect().width;
};

export interface DownloadDomElementAsPngOptions {
  scale: number;
  filename: string;
  backgroundColor?: string;
  filter?: (node: Node) => boolean;
}

export const downloadDomElementAsPng = async (
  node: HTMLElement,
  options: DownloadDomElementAsPngOptions,
): Promise<void> => {
  const backgroundColor = options.backgroundColor ?? DEFAULT_PNG_BACKGROUND;
  const clone = node.cloneNode(true) as HTMLElement;
  clone.style.position = "fixed";
  clone.style.left = "-99999px";
  clone.style.top = "0";
  clone.style.boxSizing = "content-box";
  clone.style.display = "flex";
  clone.style.flexDirection = "column";
  clone.style.gap = HERALD_CHAT_PNG_CAPTURE_GAP;
  clone.style.minWidth = "0";
  clone.style.width = `${resolveCaptureRootWidthPx(node)}px`;
  clone.style.padding = HERALD_CHAT_PNG_CAPTURE_PADDING_PX;
  clone.setAttribute("aria-hidden", "true");

  document.body.appendChild(clone);
  let dataUrl: string;
  try {
    dataUrl = await domToPng(clone, {
      scale: options.scale,
      backgroundColor,
      ...(options.filter !== undefined ? { filter: options.filter } : {}),
    });
  } finally {
    document.body.removeChild(clone);
  }

  const link = document.createElement("a");
  link.download = options.filename;
  link.href = dataUrl;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
